import type { InputHTMLAttributes } from 'react'
import React from 'react'
import { css, jsx } from '@emotion/react'
import classNames from 'classnames'

const CHECKBOX_SIZE_MAP: Record<NonNullable<CheckboxProps['size']>, number> = {
  small: 14,
  medium: 16,
}

export type CheckboxProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'size'
> & {
  isHalfChecked?: boolean
  size?: 'small' | 'medium'
}

export const Checkbox = ({
  checked,
  className,
  style,
  isHalfChecked,
  size = 'medium',
  ...props
}: CheckboxProps) => {
  let boxClassName
  if (props.disabled) {
    boxClassName = classNames(
      'stroke-shade-40',
      checked || (isHalfChecked && 'fill-shade-40')
    )
  } else {
    boxClassName =
      checked || isHalfChecked
        ? 'stroke-primary fill-primary fill-primary-dark-child-effect stroke-primary-dark-child-effect'
        : 'stroke-shade-30 stroke-shade-10-child-effect fill-shade-60-child-effect'
  }

  return jsx(
    'span',
    {
      onClick: props.onClick,
      className: classNames(
        className,
        'transition flexbox-inline flexbox-centered flexbox-no-shrink position-relative radius-normal full-white',
        'child-effects-on-hover'
      ),
      css: css({
        '&:focus-within': {
          boxShadow: '0 0 0 2px var(--focus-outline-color)',
        },
      }),
      style: {
        ...style,
        width: CHECKBOX_SIZE_MAP[size],
        height: CHECKBOX_SIZE_MAP[size],
      },
    },
    <>
      {/* We use an SVG for the box because it was a 1.5px border thickness which is impossible on regular HTML */}
      <svg
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 16 16"
        className={classNames('transition position-coverer', boxClassName)}
      >
        <rect
          x=".75"
          y=".75"
          width="14.5"
          height="14.5"
          rx="3.25"
          strokeWidth="1.5"
        />
      </svg>
      {isHalfChecked ? (
        <svg
          width={10}
          height={10}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 10 2"
          className={classNames('transition position-relative')}
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M.5 1c0-.414.318-.75.71-.75h7.58c.392 0 .71.336.71.75s-.318.75-.71.75H1.21C.819 1.75.5 1.414.5 1Z"
            fill="currentColor"
          />
        </svg>
      ) : (
        <svg
          width={10}
          height={10}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 10 8"
          className={classNames('transition position-relative', {
            'opacity-null': !checked,
          })}
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M3.123 7.352a.75.75 0 0 0 1.157.118l5.48-5.48A.75.75 0 0 0 8.7.929l-4.95 4.95-2.159-2.16A.75.75 0 1 0 .531 4.78l2.474 2.475a.754.754 0 0 0 .118.097Z"
            fill="currentColor"
          />
        </svg>
      )}
      <input
        {...props}
        type="checkbox"
        className={classNames(
          'opacity-null position-coverer',
          props.disabled ? 'cursor-forbidden' : 'pointer'
        )}
      />
    </>
  )
}
